import { appstr } from "../utils/appstr";
import { appstrp } from "../utils/appstrp";
import { convertStdDateString } from "../utils/convertStdDateString";
import {
  enumBoxOverlay,
  enumObjClassNames,
  enumObjectTypes,
  enumOverrideType,
  enumUIModes,
  appRoutes,
  kAccountADPBasic,
  kAdminUser,
  kEmployeePositionID,
  kTabMgrWidth,
  kUserEditRoles,
  kHeadcount,
  kPositionCount,
  objTypeClassMap
} from "../constants";
import { QLoadOverrideRFC } from "./QLoadOverrideRFC.js";
import { QPollTalentRFC } from "./QPollTalentRFC.js";
import { PollForPlanChanges } from "../utils/PollForPlanChanges";
import { QAlert } from "./QAlert";
import { QChange } from "./QChange";
import { QLoadRecordRFC } from "./QLoadRecordRFC";
import { QObjDBObject } from "./QObjDBObject";
import { QRecordLookupRFC } from "./QRecordLookupRFC";
import { SerializeOverrideObj } from "../utils/SerializeOverrideObj";
import { GetUnknownErrorMessage } from "../utils/GetUnknownErrorMessage";
import { IsPositionClosed } from "../utils/IsPositionClosed";
import { Globals } from "./Globals";
import { QLoadPlanSubmissionsRFC } from "./QLoadPlanSubmissionRFC";
import { QUpdatePlansRFC } from "./QUpdatePlansRFC";

/**
 * @typedef {import('./QOverrideMgr').QOverrideMgr} QOverrideMgr
 * @typedef {import('./QXDoc').QXDoc} QXDoc
 * @typedef {import('./QXWSMgr').QXWSMgr} QXWSMgr
 */
export class QPlanManager {
  handleCreatePosition(arg0, change) {
    throw new Error("Method not implemented.");
  }

  handleClonePosition(arg0, change) {
    throw new Error("Method not implemented.");
  }

  handleAssignments(arg0, change) {
    throw new Error("Method not implemented.");
  }

  constructor() {
    /**
     * @type {(QXWSMgr | null)}
     */
    this.fWSMgr = null;
    this.fTrackList = null;
    this.fActiveHover = false;
    this.fPlanInfo = null;
    this.fRootDBObj = null;
    this.fTalentPoolList = [];
    this.fIsRecalculatingFormulas = false;
    this.fPollListener = null;
    this.fTopicListeners = [];
    this.fCloneBaseCache = {};
    this.fPositionCloneBaseCache = {};
    this.fRequestedRecordIDs = new Set();
    this.fPickListMismatch = [];
    /**
     * @type {(null | QOverrideMgr)}
     */
    this.fOverrideClass = null;
    this.handleRecord = null;
    this.handleConflict = null;
    this.fPlanSubmission = null;
    this.fApprovalSettings = null;
  }

  lastActiveChangedField(theOverride, field, getPreviousValue, getDetailed) {
    if (!theOverride) return null;
    var changeHistory = theOverride.fChanges;
    for (var index = changeHistory.length - 1; index >= 0; index--) {
      var mostRecentChange = changeHistory[index];
      if (mostRecentChange.fDelta == null) return null;
      var deltaKeys = Object.keys(mostRecentChange.fDelta);
      if (deltaKeys.includes(field) && mostRecentChange.IsReverted() == false) {
        if (getPreviousValue) {
          getPreviousValue = !getPreviousValue;
          continue;
        }
        if (getDetailed) {
          return { index, mostRecentChange };
        }
        return mostRecentChange;
      }
    }
    return null;
  }

  CloneDeltas(theOverride) {
    if (!theOverride) return null;
    var changeHistory = theOverride.fChanges;
    var deltaList = changeHistory.map(function (item) {
      return item.fDelta;
    });
    return JSON.parse(JSON.stringify(deltaList));
  }

  allActiveChanges(theOverride) {
    var activeChanges = [];
    if (!theOverride) return activeChanges;
    var changeHistory = theOverride.fChanges;
    for (var j = changeHistory.length - 1; j >= 0; j--) {
      var mostRecentChange = changeHistory[j];
      if (mostRecentChange.IsReverted() == false) activeChanges.push(mostRecentChange);
    }
    return activeChanges;
  }

  // Passed a field, and a list of active changes sorted new -> old
  currentActiveValue(theField, activeChanges) {
    if (theField == null || activeChanges == null) return null;
    for (var changeIdx in activeChanges) {
      var aChange = activeChanges[changeIdx];
      if (aChange.fDelta && aChange.fDelta[theField]) return aChange.fDelta[theField];
    }
    return null;
  }

  OverrideHasConflicts(overrideObj) {
    var overrideClass = this.fOverrideClass;
    var filteredObjs = overrideClass.fObjMgr.GetFilteredObjList();
    if (filteredObjs.indexOf(overrideObj) > -1) return true;
    return false;
  }

  GetChangeType(theChange) {
    var classDef = theChange.GetParentClass();
    var typeDef = classDef.GetFieldDefByName("Type");
    var theType = theChange.GetFieldValue(typeDef, false);

    return theType;
  }

  /**
   *
   * @param {QObjDBObject} dbObj
   * @returns {(QObjDBObject | null)}
   */
  GetActiveOverrideObj(dbObj) {
    const t = this.fTrackList;
    const objType = dbObj.InferObjectType();
    const recordId = dbObj.GetObjectRecordID();

    const overrideObjList = t.FindMatchingObjectsByPredicate(override => {
      const overrideObjType = override.SmartGetFieldValue("objType") ?? "Holder";
      if (overrideObjType !== objType) {
        return false;
      }

      const overrideRefID = override.SmartGetFieldValue("RefID");
      return overrideRefID === recordId;
    });

    if (overrideObjList.length > 0) {
      for (var i in overrideObjList) {
        var anOverrideObj = overrideObjList[i];
        var activeOverride = anOverrideObj.lastActiveChange();
        if (activeOverride !== null) return anOverrideObj;
      }
    }
    return null;
  }

  /**
   *
   * @param {QObjDBObject} dbObj
   * @returns
   */
  GetActiveOverrideOrCreate(dbObj) {
    let overrideObj = this.GetActiveOverrideObj(dbObj);
    if (overrideObj == null) {
      overrideObj = this.CreateOverride(
        enumOverrideType.change,
        dbObj.InferObjectType(),
        dbObj.GetObjectRecordID(),
        "",
        []
      );
    }

    return overrideObj;
  }

  SortChangeDate(a, b, reverse) {
    var change1 = a.lastActiveChange();
    var change2 = b.lastActiveChange();

    var date1Str = change1 ? change1.GetChangeDate() : 0;
    var date2Str = change2 ? change2.GetChangeDate() : 0;

    var d1 = date1Str !== 0 ? convertStdDateString(date1Str) : new Date(date1Str);
    var d2 = date2Str !== 0 ? convertStdDateString(date2Str) : new Date(date2Str);

    return reverse ? d1 - d2 : d2 - d1;
  }

  SortChangeEffectiveDate(a, b, reverse) {
    //Plan uses effective dating--- it's super effective!

    var change1 = a.lastActiveChange();
    var change2 = b.lastActiveChange();

    var d1 = change1 ? change1.GetChangeEffectiveDate() : 0;
    var d2 = change2 ? change2.GetChangeEffectiveDate() : 0;

    //var d1 = date1Str !== 0 ? convertStdDateString(date1Str) : new Date(date1Str);
    //var d2 = date2Str !== 0 ? convertStdDateString(date2Str) : new Date(date2Str);

    return reverse ? d1 - d2 : d2 - d1;
  }

  InitPlanManager(wsMgr, plan, overrides) {
    this.fWSMgr = wsMgr;
    wsMgr.fPlanManager = this;
    delete plan["reloadplans"]; //Compenstate for bad data
    this.fPlanInfo = plan;
    var doc = this.GetDoc();
    //var scm = doc.GetSubChartMgr();
    //var sc = scm.GetActiveSubChart();
    //var rootObj = sc.GetTopDBObj();
    //rootObj.fCloneBase = rootObj.Clone(true);

    // Migrate position chart metrics from Headcount to Position Count
    const isPositionDoc = doc?.IsPositionDocument?.();
    const hasMetrics = Array.isArray(plan.metrics);
    if (isPositionDoc && hasMetrics) {
      let needsUpdate = false;
      plan.metrics.forEach(metric => {
        if (metric.fieldID === kHeadcount) {
          metric.fieldID = kPositionCount;
          needsUpdate = true;
        }
      });

      // If we migrated any metrics, send update to server
      if (needsUpdate && this.CanUpdatePlan()) {
        const savePlansRFC = new QUpdatePlansRFC();
        const accountName = wsMgr.GetAccountName();
        const user = wsMgr.GetUserName();
        savePlansRFC.InitUpdatePlans(wsMgr);
        const curPlanObj = JSON.parse(JSON.stringify(plan));
        curPlanObj["metrics"] = plan.metrics;
        savePlansRFC.SetParams(accountName, user, "saveplan", JSON.stringify(curPlanObj));
        Globals._rfcMgr.QueueRFC(savePlansRFC);
      }
    }

    var overrideClass = doc.GetOverrideClass();
    this.fTrackList = overrideClass.GetObjMgr();
    this.ResetTrackMgr();
    this.ActivateListener();
    this.fCloneBaseCache = {};
    this.fPositionCloneBaseCache = {};
    this.fRequestedRecordIDs = new Set();

    this.fOverrideClass = overrideClass;
    this.UpdateContext();
  }

  LoadOverrides(overrides) {
    this.ResetTrackMgr();
    var overrideClass = this.fOverrideClass;

    for (var i = 0; i < overrides.length; i++) {
      var theOverride = overrides[i];
      var changeType = theOverride["type"];
      var id = theOverride["id"];
      var objectType = theOverride["objType"] ?? enumObjectTypes.holder;
      var override_id = theOverride["override_id"];
      var comment = theOverride["comment"];
      var changes = theOverride["changes"];
      var displayObj = theOverride["displayObject"];
      var fromOutsidePlan = theOverride["fromOutsidePlan"] ?? false;
      var changeObj = this.CreateOverride(
        changeType,
        objectType,
        id,
        comment,
        changes,
        override_id,
        displayObj,
        fromOutsidePlan
      );
      changeObj.ClearInvalidChanges();
      overrideClass.fObjMgr.InsertObject(changeObj);
      const auxOverrides = this.CreateAuxOverrides(
        changeType,
        objectType,
        id,
        comment,
        changes,
        override_id,
        displayObj
      );

      for (const auxChange of auxOverrides) {
        auxChange.ClearInvalidChanges();
        overrideClass.fObjMgr.InsertObject(auxChange);
      }
    }

    var missingIDs = overrideClass.fObjMgr.fObjectList
      .filter(function (a) {
        return !a.SmartGetFieldValue("displayObject");
      })
      .map(function (a) {
        return a.SmartGetFieldValue("RefID");
      });

    const doc = this.GetDoc();
    const objDB = doc.GetObjDB();
    const boxClass = objDB.GetClassByName("Box");
    const idDef = boxClass.GetRecordIDFieldDef();
    const idFieldName = idDef.GetFieldName();

    const mismatches = this.GetPickListMismatch();
    for (const mismatch of mismatches) {
      missingIDs.push(mismatch[idFieldName]);
    }

    if (missingIDs.length) {
      //TODO: This needs to either call the dedicated planning version, or this RFC needs to be cross
      //compatible with planning.
      var lookupRFC = new QLoadRecordRFC();
      lookupRFC.InitLoadRecordRFC({ planMgr: this });
      var accountName = this.fWSMgr.GetAccountName();
      var userID = this.fWSMgr.GetUserName();
      var data = {
        planID: this.GetPlanID(),
        recordIDs: missingIDs,
        includeSupervisor: false
      };
      lookupRFC.SetParams(accountName, userID, JSON.stringify(data));
      Globals._rfcMgr.QueueRFC(lookupRFC);
    }

    const objectList = overrideClass.fObjMgr.fObjectList;
    this.LoadMissingReferencedRecords(objectList, boxClass, doc);

    this.fWSMgr.BroadcastTopic("loadedOverrides", { planMgr: this });
  }

  SetTopObjRef(jsonObj) {
    const doc = this.GetDoc();
    const hierarchyClass = doc.GetHierarchyClass();
    const dbObj = new QObjDBObject();
    dbObj.InitObject(hierarchyClass);
    hierarchyClass.ProcessObject(dbObj, jsonObj);

    this.fRootDBObj = dbObj;
  }

  AttachHolderRecords(positionObj, holderRecords) {
    if (!holderRecords || !holderRecords.length) {
      return;
    }
    const boxClass = this.GetDoc().GetObjDB().GetClassByName("Box");
    positionObj.fHolderList = holderRecords.map(record => {
      const holder = new QObjDBObject();
      holder.InitObject(boxClass);
      boxClass.ProcessObject(holder, record);
      holder.SetParentPosition(positionObj);
      return holder;
    });
  }

  SetCoHeadIDs(coHeadIds) {
    var planMetadata = this.fPlanInfo;
    if (planMetadata["doCoHead"] && coHeadIds && coHeadIds.length > 0) {
      planMetadata.fCoHeadIDs = coHeadIds;
    }
  }

  IsCoHeadPlan() {
    var planMetadata = this.fPlanInfo;
    return (
      planMetadata["doCoHead"] && planMetadata.fCoHeadIDs && planMetadata.fCoHeadIDs.length > 0
    );
  }

  GetCoHeadIDs() {
    var coHeadIDs = [];
    var planMetadata = this.fPlanInfo;
    if (this.IsCoHeadPlan()) {
      return planMetadata.fCoHeadIDs;
    }
    return coHeadIDs;
  }

  GetTopObjRef() {
    return this.fRootDBObj;
  }

  UpdateContext() {
    this.fWSMgr?.fPlanMgrContextStateFn({ state: this });
  }

  /**
   *
   * @param {*} data
   * @param {function=} resolve
   */
  LoadRecords(data, resolve = undefined) {
    data = { planID: this.GetPlanID(), includeSupervisor: true, ...data };

    const accountName = this.fWSMgr.GetAccountName();
    const username = this.fWSMgr.GetUserName();
    const loadRecordRFC = new QLoadRecordRFC();
    const callbackObj = {
      planMgr: this,
      resolve
    };
    loadRecordRFC.InitLoadRecordRFC(callbackObj);
    loadRecordRFC.SetParams(accountName, username, JSON.stringify(data));
    Globals._rfcMgr.QueueRFC(loadRecordRFC);
  }

  LoadOutsidePlanRecords(recordIDs, resolve, handlePositions) {
    var w = this.fWSMgr;
    var recordLookup = new QRecordLookupRFC();
    recordLookup.InitRecordLookup(null, resolve);
    recordLookup.SetParams(
      w.GetAccountName(),
      recordIDs,
      w.GetDoc()?.GetChartID(),
      w.GetUserName(),
      handlePositions
    );
    Globals._rfcMgr.QueueRFC(recordLookup);
  }

  ActivateListener() {
    this.fPollListener = setTimeout(function () {
      PollForPlanChanges(true);
    }, 5000);

    const wsMgr = this.fWSMgr;
    const unsubscribe = wsMgr.SubscribeOnceToTopic("SuspendPolling", () => {
      this.SuspendListener();
    });

    this.fTopicListeners.push(unsubscribe);
  }

  SuspendListener() {
    clearTimeout(this.fPollListener);

    const wsMgr = this.fWSMgr;
    const unsubscribe = wsMgr.SubscribeOnceToTopic("ResumePolling", () => {
      this.ActivateListener();
    });

    this.fTopicListeners.push(unsubscribe);
  }

  ResetTrackMgr() {
    this.fTrackList.PurgeData();
  }

  GetPlanInfo() {
    return this.fPlanInfo;
  }

  GetPlanMasterChartID() {
    return this.GetPlanInfo()["chartID"];
  }

  SetSyncDate(syncDate) {
    this.fPlanInfo["syncDate"] = syncDate;
  }

  IsLiveSync() {
    return this.fPlanInfo["liveSync"];
  }

  GetLastSyncLabel(timeOnly = false) {
    var syncDate = this.fPlanInfo["syncDate"];
    var d2 = new Date();
    var localizedSyncDate = d2.convertUTCDateToLocalDateString(syncDate, null, true);

    var syncTimeStr = syncDate
      ? localizedSyncDate
      : this.IsLiveSync()
        ? appstr("Active")
        : appstr("Never");

    if (timeOnly) return syncTimeStr;

    var syncLabel = appstrp("Last Sync", ": ");
    return syncLabel + syncTimeStr;
  }

  UpdateFields(fields) {
    this.fPlanInfo["fields"] = fields;
    this.UpdateContext();
  }

  IsPositionPlan() {
    const planMetadata = this.fPlanInfo;
    return planMetadata.isPositions === true;
  }

  GetChangeList() {
    return this.fTrackList.GetObjectList();
  }

  GetPlanName() {
    return this.fPlanInfo["name"];
  }

  SetPlanName(name) {
    this.fPlanInfo["name"] = name;
  }

  GetPlanID() {
    return this.fPlanInfo["ID"];
  }

  GetPlanDescription() {
    return this.fPlanInfo["description"];
  }

  SetPlanDescription(description) {
    this.fPlanInfo["description"] = description;
  }

  GetPlanObjective() {
    return this.fPlanInfo["objective"];
  }

  SetPlanObjective(objective) {
    this.fPlanInfo["objective"] = objective;
  }

  GetTalentPool() {
    return this.fTalentPoolList;
  }

  GetPickListMismatch() {
    return this.fPickListMismatch;
  }

  SetPickListMismatch(pickRecords) {
    this.fPickListMismatch = pickRecords;
  }

  GetIsRecalculatingFormulas() {
    return this.fIsRecalculatingFormulas;
  }

  SetIsRecalculatingFormulas(isRecalc) {
    this.fIsRecalculatingFormulas = isRecalc;
  }

  // Override hash
  GetOverrideHash() {
    return this.fOverrideHash;
  }

  SetOverrideHash(md5) {
    this.fOverrideHash = md5;
  }

  // Talent Pool hash
  GetTalentPoolHash() {
    return this.fTalentPoolHash;
  }

  SetTalentPoolHash(md5) {
    this.fTalentPoolHash = md5;
  }

  // Formula Timestamp
  GetFormulaTimestamp() {
    return this.fFormulaTimestamp;
  }

  SetFormulaTimestamp(timestamp) {
    this.fFormulaTimestamp = timestamp;
  }

  GetPlanSubmissionHash() {
    return this.fPlanSubmissionHash;
  }

  SetPlanSubmissionHash(md5) {
    this.fPlanSubmissionHash = md5;
  }

  UpdatePlanInfoFromResponse(responseData) {
    if (!this.fPlanInfo || !responseData) return null;

    const keysToUpdate = ["name", "status", "description", "objective", "effectiveDates"];
    const changedKeys = [];

    keysToUpdate.forEach(key => {
      if (responseData[key] !== undefined && this.fPlanInfo[key] !== responseData[key]) {
        this.fPlanInfo[key] = responseData[key];
        changedKeys.push(key);
      }
    });

    return changedKeys.length > 0 ? changedKeys : null;
  }

  UpdateTalentPool(talentPool) {
    this.fTalentPoolList = talentPool;
  }

  GetSharedUsers() {
    return this.fPlanInfo["users"];
  }

  CanUpdatePlan() {
    var myPlanRole = this.GetPlanRole();
    return kUserEditRoles.indexOf(myPlanRole) > -1 && this.GetPlanStatus() === "Active";
  }

  IsAdmin() {
    var userInfo = this.fWSMgr.GetUserInfo();
    var myUserAccountRole = userInfo.GetUserRole();
    return myUserAccountRole === kAdminUser;
  }

  GetPlanRole() {
    var curUser = this.fWSMgr.GetUserName();
    var userList = this.GetSharedUsers();
    for (var i = 0; i < userList.length; i++) {
      if (userList[i].name === curUser) {
        return userList[i].role;
      }
    }
    return false;
  }

  UpdateMetrics(newMetrics) {
    this.fPlanInfo["metrics"] = newMetrics;
  }

  GetPlanStatus() {
    return this.fPlanInfo["status"];
  }

  GetUseEffectiveDates() {
    return this.fPlanInfo["effectiveDates"] === true;
  }

  GetPlanSubmission() {
    return this.fPlanSubmission;
  }

  SetPlanSubmission(planSubmission) {
    this.fPlanSubmission = planSubmission;
  }

  LoadPlanSubmission() {
    const org = this.fWSMgr.GetAccountName();
    const user = this.fWSMgr.GetUserName();
    const callBackObj = {
      fWSMgr: this.fWSMgr
    };
    const loadPlanSubmissionsRFC = new QLoadPlanSubmissionsRFC();
    loadPlanSubmissionsRFC.InitLoadPlanSubmissions(callBackObj);
    const submissionRequestData = { planID: this.GetPlanID() };
    loadPlanSubmissionsRFC.SetParams(
      org,
      user,
      "getPlanSubmission",
      JSON.stringify(submissionRequestData)
    );

    Globals._rfcMgr.QueueRFC(loadPlanSubmissionsRFC);
  }

  GetApprovalSettings() {
    return this.fApprovalSettings;
  }

  SetApprovalSettings(approvalSettings) {
    this.fApprovalSettings = approvalSettings;
  }

  /**
   * @returns {(QXDoc | null)}
   */
  GetDoc() {
    //TODO: This is weird scope for this fxn
    var theDoc = this.fWSMgr.GetDoc();
    return theDoc;
  }

  GetChangesOfType(type) {
    var t = this.fTrackList.fObjectList;
    //var classDef = t.fClass;

    var response = t.filter(function (a) {
      return a.SmartGetFieldValue("Type") == type;
    });

    return response;
  }

  ObjectIDTrackedByType(dbObjID, type) {
    var obj = this.ObjectIDTracked(dbObjID);
    if (obj == null || obj.SmartGetFieldValue("Type") !== type) return null;
    return obj;
  }

  ObjectIDTracked(dbObjID) {
    var t = this.fTrackList;
    var classDef = t.fClass;

    var dbIDFieldDef = classDef.GetFieldDefByName("RefID");
    var obj = t.FindObject(dbIDFieldDef, dbObjID, false);

    return obj;
  }

  AssignIconToBox(dbObj) {
    var trackObj = this.GetActiveOverrideObj(dbObj);
    if (trackObj) {
      var theType = this.GetChangeType(trackObj);
      if (this.GetUseEffectiveDates()) {
        var suppress = true;
        for (var changeIdx in trackObj.fChanges) {
          var theChange = trackObj.fChanges[changeIdx];
          if (theChange.IsDateEffective()) suppress = false;
        }
        if (suppress) return null;
      }
      switch (theType) {
        case enumOverrideType.change:
          return enumBoxOverlay.change;
        case enumOverrideType.add:
          return enumBoxOverlay.add;
        case enumOverrideType.move:
          return enumBoxOverlay.move;
        case enumOverrideType.comment:
          return enumBoxOverlay.comment;
        case enumOverrideType.delete:
          return enumBoxOverlay.delete;
        case enumOverrideType.moveout:
          return enumBoxOverlay.moveout;
      }
    }
    return null;
  }

  HandleLoadRecordResponse(success, baseHolders, basePositions) {
    const doc = this.GetDoc();
    const objDB = doc.GetObjDB();
    const boxClass = objDB.GetClassByName(enumObjClassNames.box);
    const positionClass = objDB.GetClassByName(enumObjClassNames.position);

    const holders = baseHolders || [];
    const positions = basePositions || [];

    if (success) {
      const holdersByPositionId = {};
      for (let result of holders) {
        const newCloneBase = new QObjDBObject();
        newCloneBase.InitObject(boxClass);
        boxClass.ProcessObject(newCloneBase, result);
        const holderId = newCloneBase.GetObjectRecordID();
        this.fCloneBaseCache[holderId] = newCloneBase.Clone(true);

        const override = this.fOverrideClass?.GetExistingBoxOverride(newCloneBase);
        if (override) {
          override.ApplyChangesReadOnly(newCloneBase);
        }

        const positionId = newCloneBase.SmartGetFieldValue(kEmployeePositionID);
        if (positionId) {
          if (!holdersByPositionId[positionId]) {
            holdersByPositionId[positionId] = [];
          }
          holdersByPositionId[positionId].push(newCloneBase);
        }
      }

      if (doc.IsPositionDocument()) {
        for (let result of positions) {
          const newCloneBase = new QObjDBObject();
          newCloneBase.InitObject(positionClass);
          positionClass.ProcessObject(newCloneBase, result);
          const positionId = newCloneBase.GetObjectRecordID();
          this.fPositionCloneBaseCache[positionId] = newCloneBase.Clone(true);

          const holders = holdersByPositionId[positionId] || [];
          if (holders.length > 0) {
            this.fWSMgr?.BroadcastTopic("positionLoadedWithHolders", {
              positionId: positionId,
              holders: holders
            });
          }
        }
      }
    }

    var overrideClass = this.fOverrideClass;
    var objectList = overrideClass.GetObjMgr().fObjectList;

    for (let object of objectList) {
      const refId = object.SmartGetFieldValue("RefID");
      const objType = object.SmartGetFieldValue("objType");
      const baseRecord = this.GetCacheBaseRecord(objType, refId);
      if (baseRecord) {
        object.SmartSetFieldValue("displayObject", baseRecord.Clone(true));
      }
    }

    if (success) {
      this.LoadMissingReferencedRecords(objectList, boxClass, doc);
    }

    this.UpdateContext();
  }

  /**
   * Loads records referenced by overrides that are missing from cache,
   * so change display can resolve labels for previous and new values.
   */
  LoadMissingReferencedRecords(objectList, boxClass, doc) {
    const referencedIDs = new Set();
    let cache;
    let loadKey;

    if (doc.IsPositionDocument()) {
      const empPosFieldDef = boxClass?.GetFieldDefByID(kEmployeePositionID);
      const empPosFieldName = empPosFieldDef?.GetFieldName();
      if (!empPosFieldName) return;

      this.CollectReferencedFieldIDs(
        objectList,
        empPosFieldName,
        kEmployeePositionID,
        referencedIDs
      );
      cache = this.fPositionCloneBaseCache;
      loadKey = "positionIDs";
    } else {
      const supervisorFieldDef = boxClass?.GetSupervisorIDFieldDef?.();
      const supervisorFieldName = supervisorFieldDef?.GetFieldName();
      if (!supervisorFieldName) return;

      const supervisorFieldID = supervisorFieldDef.GetFieldID();
      this.CollectReferencedFieldIDs(
        objectList,
        supervisorFieldName,
        supervisorFieldID,
        referencedIDs
      );
      cache = this.fCloneBaseCache;
      loadKey = "recordIDs";
    }

    const missingIDs = [...referencedIDs].filter(
      id => cache[id] === undefined && !this.fRequestedRecordIDs.has(id)
    );

    if (missingIDs.length > 0) {
      for (const id of missingIDs) {
        this.fRequestedRecordIDs.add(id);
      }
      this.LoadRecords({ [loadKey]: missingIDs, includeSupervisor: false }, () => {
        for (const id of missingIDs) {
          this.fRequestedRecordIDs.delete(id);
        }
      });
    }
  }

  CollectReferencedFieldIDs(objectList, fieldName, fieldID, outSet) {
    for (const object of objectList) {
      const displayObj = object.SmartGetFieldValue("displayObject");
      const prevId = displayObj?.SmartGetFieldValue?.(fieldID);
      if (prevId) outSet.add(prevId);

      const changes = object.fChanges || [];
      for (const change of changes) {
        const newId = change.fDelta?.[fieldName];
        if (newId) outSet.add(newId);
      }
    }
  }

  /**
   *
   * @param {string} objType
   * @param {string} refId
   * @returns {(QObjDBObject | null)}
   */
  GetCacheBaseRecord(objType, refId) {
    if (objType === enumObjectTypes.position) {
      return this.fPositionCloneBaseCache[refId] ?? null;
    }

    return this.fCloneBaseCache[refId] ?? null;
  }

  /**
   * @param {string} objType
   * @param {string} refId
   * @returns {boolean}
   */
  HasCacheEntry(objType, refId) {
    if (objType === enumObjectTypes.position) {
      return Object.hasOwn(this.fPositionCloneBaseCache, refId);
    }

    return Object.hasOwn(this.fCloneBaseCache, refId);
  }

  /**
   * For requested IDs not returned by the server, populates the cache
   * with the override's displayObject (covers records fabricated by Add overrides).
   * Sets null for IDs with no displayObject, signaling "load attempted, not found."
   *
   * @param {string[]} positionIDs
   * @param {string[]} recordIDs
   */
  PopulateCacheFromOverrides(positionIDs, recordIDs) {
    const t = this.fTrackList;
    const outsidePlanPositionIDs = [];

    for (const id of positionIDs) {
      if (this.fPositionCloneBaseCache[id] !== undefined) continue;
      const override = t.FindMatchingObjectsByPredicate(
        o =>
          o.SmartGetFieldValue("RefID") === id &&
          (o.SmartGetFieldValue("objType") ?? enumObjectTypes.holder) === enumObjectTypes.position
      )[0];
      const displayObj = override?.SmartGetFieldValue("displayObject");
      if (displayObj) {
        this.fPositionCloneBaseCache[id] = displayObj.Clone(true);
      } else {
        outsidePlanPositionIDs.push(id);
      }
    }

    // Retry positions the plan API couldn't return — they may be out of scope
    if (outsidePlanPositionIDs.length > 0) {
      for (const id of outsidePlanPositionIDs) {
        this.fRequestedRecordIDs.add(id);
      }
      this.LoadRecords({ positionIDs: outsidePlanPositionIDs, outsidePlan: true }, () => {
        for (const id of outsidePlanPositionIDs) {
          this.fRequestedRecordIDs.delete(id);
          if (this.fPositionCloneBaseCache[id] === undefined) {
            this.fPositionCloneBaseCache[id] = null;
          }
        }
        this.UpdateContext();
      });
    }

    for (const id of recordIDs) {
      if (this.fCloneBaseCache[id] !== undefined) continue;
      const override = t.FindMatchingObjectsByPredicate(
        o =>
          o.SmartGetFieldValue("RefID") === id &&
          (o.SmartGetFieldValue("objType") ?? enumObjectTypes.holder) === enumObjectTypes.holder
      )[0];
      const displayObj = override?.SmartGetFieldValue("displayObject");
      this.fCloneBaseCache[id] = displayObj?.Clone(true) ?? null;
    }
  }

  CreateOverride(
    changeType,
    objectType,
    id,
    comment,
    changes,
    override_id,
    displayObj,
    fromOutsidePlan = false
  ) {
    const wsMgr = this.fWSMgr;
    const overrideClass = this.fOverrideClass;
    const objDB = overrideClass.GetObjDB();
    const doc = objDB.GetDocRef();
    const hierarchyClass = doc.GetHierarchyClass();
    const superField = hierarchyClass.GetSupervisorIDFieldDef();
    let displayValue = displayObj;

    const newOverride = new QObjDBObject();
    newOverride.InitObject(overrideClass);
    newOverride.InitNewObject(overrideClass);
    newOverride.SmartSetFieldValue("Type", changeType);
    newOverride.SmartSetFieldValue("objType", objectType);
    newOverride.SmartSetFieldValue("RefID", id);
    newOverride.SmartSetFieldValue("override_id", override_id);
    newOverride.SmartSetFieldValue("fromOutsidePlan", fromOutsidePlan);

    const baseCacheRecord = this.GetCacheBaseRecord(objectType, id);

    var dbObj = overrideClass.GetBaseRecord(newOverride);
    var refObjDef = overrideClass.GetFieldDefByID("RefObj");
    if (dbObj) {
      newOverride.AddNewFieldValue(refObjDef, dbObj);
      var clone = dbObj.Clone(true);
      if (dbObj.fCloneBase == null) {
        dbObj.fCloneBase = clone;
      }
      displayValue = clone;
    } else if (baseCacheRecord) {
      displayValue = baseCacheRecord.Clone(true);
    } else if (displayObj) {
      var normalizedRec = doc.NormalizeDisplayRecord(displayObj, objectType);
      let objClass = objDB.GetClassByName(enumObjClassNames.box);
      if (objectType === enumObjectTypes.position) {
        objClass = objDB.GetClassByName(enumObjClassNames.position);
      }
      var refObj = new QObjDBObject();
      refObj.InitObject(objClass);
      objClass.ProcessObject(refObj, normalizedRec);
      displayValue = refObj.Clone(true);
    }

    newOverride.SmartSetFieldValue("displayObject", displayValue);

    var theComment = comment;
    if (theComment) {
      var commentDef = overrideClass.GetFieldDefByID("Comment");
      newOverride.AddNewFieldValue(commentDef, theComment);
    }

    var changeList = changes;
    newOverride.fChanges = [];
    if (!changeList) changeList = [];
    var pushConflict = false;

    for (var i = 0; i < changeList.length; i++) {
      var theChange = changeList[i];
      var newChange = new QChange();
      newChange.InitChange(wsMgr, theChange, newOverride, dbObj, overrideClass);
      newChange.fPlanMgr = this;
      newOverride.fChanges.push(newChange);
      //Using the filter list to flag the number of conflicts
      if (newChange.IsConflicted() && !newChange.IsReverted()) pushConflict = true;
    }

    newOverride.ApplyChanges();

    if (pushConflict) overrideClass.fObjMgr.FilterObject(newOverride);

    if (changeType === enumOverrideType.moveout) {
      var lastChange = this.lastActiveChangedField(newOverride, superField.GetFieldName());
      if (lastChange && !lastChange.IsConflicted() && !lastChange.IsReverted()) {
        this.LoadOutsidePlanRec([lastChange.fDelta[superField.GetFieldName()]]);
      }
    }

    return newOverride;
  }

  CreateAuxOverrides(changeType, objectType, id, comment, changes, override_id, displayObj) {
    const auxOverrides = [];

    const overrideClass = this.fOverrideClass;
    const objDB = overrideClass.GetObjDB();
    const boxClass = objDB.GetClassByName("Box");

    const idFieldDef = boxClass.GetRecordIDFieldDef();
    const auxFieldDef = boxClass.GetFieldDefByID("isAux");
    const supervisorFieldDef = boxClass.GetSupervisorIDFieldDef();
    const superFieldName = supervisorFieldDef?.GetFieldName();

    if (!auxFieldDef) {
      return auxOverrides;
    }

    const auxInstances = boxClass.FindMatchingObjectsByPredicate(obj => {
      const isAuxValue = obj.GetFieldValue(auxFieldDef);
      const isAuxInstance = isAuxValue > 0;
      if (!isAuxInstance) {
        return false;
      }

      const cleanRecordId = obj.GetAuxCleanID(idFieldDef, auxFieldDef);
      return cleanRecordId === id;
    });

    for (const auxRecord of auxInstances) {
      const nextId = auxRecord.GetFieldValue(idFieldDef);
      const suffix = nextId.replace(id, "");
      const nextOverrideId = `${override_id}${suffix}`;

      const isAuxValue = auxRecord.SmartGetFieldValue("isAux");
      const parent = auxRecord.GetParent();
      const parentRecordId = parent?.GetFieldValue(idFieldDef) ?? "";
      const cleanParentId = parent?.GetAuxCleanID(idFieldDef, auxFieldDef) ?? "";
      const parentAuxValue = parent?.SmartGetFieldValue("isAux") ?? null;
      const isAuxDirectReport = isAuxValue === parentAuxValue;

      const auxChanges = changes.map(change => {
        if (!isAuxDirectReport) {
          return change;
        }

        if (change.data[superFieldName] !== cleanParentId) {
          return change;
        }

        return {
          ...change,
          data: {
            ...change.data,
            [superFieldName]: parentRecordId
          }
        };
      });

      let auxOverrideType = changeType;
      if (changeType === enumOverrideType.move) {
        auxOverrideType = enumOverrideType.change;
      }

      const overrideObj = this.CreateOverride(
        auxOverrideType,
        objectType,
        nextId,
        comment,
        auxChanges,
        nextOverrideId,
        displayObj,
        false
      );
      auxOverrides.push(overrideObj);
    }

    return auxOverrides;
  }

  LoadOutsidePlanRec(recordIds) {
    var loadRecordRFC = new QLoadRecordRFC();
    loadRecordRFC.InitLoadRecordRFC({ planMgr: this });
    loadRecordRFC.SetParams(
      this.fWSMgr.GetAccountName(),
      this.fWSMgr.GetUserName(),
      JSON.stringify({
        planID: this.GetPlanID(),
        outsidePlan: true,
        includeSupervisor: false,
        recordIDs: recordIds
      })
    );
    Globals._rfcMgr.QueueRFC(loadRecordRFC);
  }

  ClosePlanManager() {
    if (this.fInteractPanel) {
      this.fWSMgr.RemovePanel(this.fInteractPanel);
      this.fInteractPanel = null;
    }
    for (const unsubscribe of this.fTopicListeners) {
      unsubscribe();
    }

    this.fWSMgr.fPlanMgrContextStateFn({ state: null });
    this.SuspendListener();
  }

  ExecuteAlertCommand(aCmd) {
    //Conflict resolution
    var sourcePanel = aCmd.GetSourcePanel();
    var theOverride = sourcePanel.fOverrideObj;
    var theLastChange = theOverride.lastActiveChange();
    var cmdCode = aCmd.GetCmdCode();
    cmdCode === "Keep" ? (theLastChange.fConflict = false) : (theLastChange.fIsReverted = true);
    this.SendUpdate(theOverride, false);
  }

  /**
   * Resolves the new supervisor/parent dbObj for a pending Move override, or null
   * if the override isn't a move, has no resolvable target, or the target isn't
   * pending deletion in this plan.
   *
   * @param {QObjDBObject} overrideObj
   * @returns {QObjDBObject | null}
   */
  GetBlockedMoveTarget(overrideObj) {
    const overrideClass = this.fOverrideClass;
    const objDB = overrideClass?.GetObjDB();
    const doc = objDB?.GetDocRef();
    if (!doc) return null;

    const objType = overrideObj.SmartGetFieldValue("objType") ?? enumObjectTypes.holder;
    const objClass = objDB.GetClassByName(objTypeClassMap[objType]);
    const parentFieldName = objClass?.GetParentIDFieldDef?.()?.GetFieldName?.();
    if (!parentFieldName) return null;

    const matchingChange = this.lastActiveChangedField(overrideObj, parentFieldName);
    const targetID = matchingChange?.fDelta?.[parentFieldName];
    if (targetID == null) return null;

    const targetClassName = doc.IsPositionDocument()
      ? enumObjClassNames.position
      : objTypeClassMap[objType];
    const targetClass = objDB.GetClassByName(targetClassName);
    const targetObj = targetClass?.FindObject(targetClass.GetRecordIDFieldDef(), targetID, false);
    if (!targetObj) return null;

    return IsPositionClosed(targetObj) ? targetObj : null;
  }

  SendUpdate(overrideObj, onlyComment, doTalent, revertAll) {
    var wsMgr = this.fWSMgr;
    var accountName = wsMgr.GetAccountName();
    var user = wsMgr.GetUserName();

    if (!onlyComment && !revertAll) {
      const overridesToCheck = Array.isArray(overrideObj) ? overrideObj : [overrideObj];
      const isBlocked = overridesToCheck.some(anOverride => this.GetBlockedMoveTarget(anOverride));
      if (isBlocked) {
        const alert = new QAlert();
        alert.InitAlert(
          appstr("Cannot move record"),
          appstr("You cannot move this record under a closed position."),
          null,
          wsMgr
        );
        return Promise.resolve(false);
      }
    }

    var overrideRFC = new QLoadOverrideRFC();
    overrideRFC.InitLoadOverride(this, parent);
    var overrideData = SerializeOverrideObj(overrideObj);
    var mode = null;
    if (onlyComment) mode = "onlycomment";
    else if (revertAll) mode = "revert";
    else mode = "editoverride";

    var data = { planID: this.GetPlanID(), override: overrideData, mode: mode };
    if (doTalent) {
      data["talentPool"] = true;
    }
    if (overrideObj.fBranchAdd === true) data["BranchAdd"] = true;
    if (overrideObj.fRollUp) data["RollUp"] = true;
    overrideRFC.SetParams(accountName, user, JSON.stringify(data));
    const promise = overrideRFC.GetPromise();
    Globals._rfcMgr.QueueRFC(overrideRFC);
    return promise;
  }

  SimulateSaveFeedback(seconds = 3) {
    if (this.fFeedbackId) clearTimeout(this.fFeedbackId);

    var wsMgr = this.fWSMgr;
    var docMgr = wsMgr.GetDocMgr();
    docMgr.fSaveQueued = true;
    wsMgr.UpdateDocContext();

    this.fFeedbackId = setTimeout(() => {
      docMgr.fSaveQueued = false;
      wsMgr.UpdateDocContext();
    }, seconds * 1000);
  }

  DecodeError(errorCode) {
    const ERROR_MESSAGES = {
      5000: { message: "Permission denied to access planning module" },
      5001: {
        title: "Plan no longer exists",
        message: "Plan no longer exists (long)"
      },
      5002: { message: "Permission denied to access plan" },
      5003: { message: "Insufficient role to access planning module" },
      666: { message: "connectionerr" }
    };

    const DEFAULT_HEADER = "Error loading plan";

    const error = ERROR_MESSAGES[errorCode] || {};

    return {
      title: appstr(error.title || DEFAULT_HEADER),
      message: errorCode in ERROR_MESSAGES ? appstr(error.message) : GetUnknownErrorMessage()
    };
  }

  AbortPlanningMode({ errorCode, alertMessage } = {}) {
    const wsMgr = this.fWSMgr;

    let title, message;
    if (alertMessage) {
      title = appstr("Error loading plan");
      message = alertMessage;
    } else {
      ({ title, message } = this.DecodeError(errorCode));
    }

    this.PlanAlert(message, title);

    Globals._svrChartMgr.SetLastPlanID(null); // Clear out the plan ID since we aborted abruptly

    // Centralized recovery — picks user's safe default (org launch
    // doc, first allowed master, etc.). Routes via /base/{id} so
    // useDocumentLoader hydrates a real doc, not the mode-only
    // /chart limbo that left the canvas blank previously.
    if (wsMgr.FallbackToSafeChart) {
      wsMgr.FallbackToSafeChart();
    }

    this.ClosePlanManager();
    wsMgr.BroadcastTopic("LaunchChartExplorer", 3); // ChartExplorerTabs.PLANS
  }

  //================================================================================================
  // activity margin
  AddDocPubMargin(pageProps, pubMargins) {
    //Add in margin on right side to add in data
    var marginSizeH = kTabMgrWidth;
    var curDocMargins = pageProps.fMargins;
    var curMaster = this.fWSMgr.fBackgroundMgr.GetCurBackgroundName();
    if (this.fTempSavedMarginRect == null) {
      this.fTempSavedMarginRect = JSON.parse(JSON.stringify(pubMargins));
      if (curMaster != null) this.CacheTempPubMargins(pageProps, curMaster);
      pubMargins.fRight += marginSizeH;
    }
    curDocMargins.fRight = this.fTempSavedMarginRect.fRight + marginSizeH;
    return pubMargins;
  }

  AddMasterPubMargin(pageProps, masterName) {
    var marginSizeH = kTabMgrWidth;
    var masterMargins = pageProps.fMargins;
    this.CacheTempPubMargins(pageProps, masterName);
    masterMargins.fRight = this.fTempSavedMasterMargins[masterName].fRight;
    masterMargins.fRight += marginSizeH;
  }

  CacheTempPubMargins(pageProps, masterName) {
    var masterMargins = pageProps.fMargins;
    if (this.fTempSavedMasterMargins == null) this.fTempSavedMasterMargins = {};
    if (this.fTempSavedMasterMargins[masterName] == null)
      this.fTempSavedMasterMargins[masterName] = JSON.parse(JSON.stringify(masterMargins));
  }

  RestorePageContentPubMargin(pageMgr) {
    if (this.fTempSavedMasterMargins != null) {
      for (var i in this.fTempSavedMasterMargins) {
        var masterPageObj = this.fWSMgr.fBackgroundMgr.RetrieveCachedBackground(i);
        if (masterPageObj != null)
          masterPageObj["pageProps"].fMargins.fRight = this.fTempSavedMasterMargins[i].fRight;
      }
      delete this.fTempSavedMasterMargins;
    }

    if (this.fTempSavedMarginRect != null) {
      var curPageMargins = pageMgr.GetPageMargins();
      curPageMargins.fRight = this.fTempSavedMarginRect.fRight;
      delete this.fTempSavedMarginRect;
    }
  }

  IsValidOverride(newOverride) {
    return true;
  }

  //======================================================================================================================
  PollTalent() {
    var orgName = this.fWSMgr.GetAccountName();
    var user = this.fWSMgr.GetUserName();
    var talentPoll = new QPollTalentRFC();
    talentPoll.InitPollTalent(this);
    talentPoll.SetParams(orgName, user, this.GetPlanID());
    Globals._rfcMgr.QueueRFC(talentPoll);
  }

  GetManagerName() {
    return "planManager";
  }

  PlanAlert(message, title = appstr("Error loading plan")) {
    const wsMgr = this.fWSMgr;
    const w = new QAlert();
    w.InitAlert(title, message, wsMgr.GetBackgroundPanel(), wsMgr);
  }

  PlanNavigationAlert(wsMgr, id) {
    var w = new QAlert();
    w.InitConfirmAlert(
      appstr("Conflict"),
      appstr("Preexistingid") + id,
      wsMgr.GetBackgroundPanel(),
      wsMgr,
      "NavToBox",
      "exitframeless",
      "Go To Record",
      "Stay",
      { targetID: id, treeID: Globals._svrChartMgr.fTreeID }
    );
  }

  CentralResponseAlert(r) {
    var message = "";
    var title = appstr("Alert");
    var isBasic = Globals._accountInfo.GetOC5Edition() === kAccountADPBasic;
    if (r.code != null) {
      var code = r.code.toString();
      switch (code) {
        case "5007":
          message = appstr("Cannot add plan top box to talent pool");
          break;
        case "5008":
          message = appstr("Could not locate employee to add to talent pool");
          break;
        case "5009":
          message = appstr("Failed to remove talent");
          break;
        case "5010":
          message = appstr("Failed to reorder talent");
          break;
        case "9999":
          message = isBasic ? appstr("unknownerrnoemail") : GetUnknownErrorMessage();
          break;
        case "0000":
        default:
          message = "";
          break;
      }
      if (message.length > 0) {
        const alert = new QAlert();
        alert.InitAlert(title, message, null, this.fWSMgr);
      }
    }
  }

  HandleNavPrompt(response, info, wsMgr) {
    var isBoxShown = true;
    var planMetaData = response["planMetaData"];
    var overrides = planMetaData["overrides"];
    if (overrides && info["override_id"]) {
      var target_override = null;
      for (var i = 0; i < overrides.length; i++) {
        if (overrides[i]["override_id"] === info["override_id"]) {
          target_override = overrides[i];
          break;
        }
      }
      if (
        target_override != null &&
        (target_override["type"] === enumOverrideType.move ||
          target_override["type"] === enumOverrideType.add)
      ) {
        isBoxShown = wsMgr.FindDataObjByPersonID(target_override["id"]);
        if (!isBoxShown) {
          var navigationAlert = new QAlert();
          navigationAlert.InitConfirmAlert(
            appstr("navtorec"),
            appstr("recnavprompt"),
            wsMgr.fCanvasPanel,
            wsMgr,
            "NavToBox",
            "exitframeless",
            "Go To Record",
            "Stay",
            { targetID: target_override["id"], treeID: Globals._svrChartMgr.fTreeID }
          );
        }
      }
    }
  }

  UpdatePlanState(planMetaData) {
    var hashes = planMetaData["hashes"];
    this.SetTalentPoolHash(hashes["talentPoolmd5"]);
    this.SetOverrideHash(hashes["overridemd5"]);
    this.SetPlanSubmissionHash(hashes["planSubmissionmd5"]);
    this.SetFormulaTimestamp(hashes["formulaTimestamp"]);
    if (planMetaData["pickListMismatch"])
      this.SetPickListMismatch(planMetaData["pickListMismatch"]);
    if (planMetaData["overrides"]) this.LoadOverrides(planMetaData["overrides"]);
    if (planMetaData["planSubmission"]) this.SetPlanSubmission(planMetaData["planSubmission"]);
    if (planMetaData["planTopBoxRef"]) this.SetTopObjRef(planMetaData["planTopBoxRef"]);
    if (planMetaData["planTopBoxRef"] && planMetaData["planTopBoxHolderRecords"]) {
      this.AttachHolderRecords(this.fRootDBObj, planMetaData["planTopBoxHolderRecords"]);
    }
    this.SetIsRecalculatingFormulas(hashes["recalculatingFormulas"]);
    if (planMetaData["approvalSettings"]) {
      this.SetApprovalSettings(planMetaData["approvalSettings"]);
    }
  }

  /**
   * @param {import("../constants").TBoxActions | null} action
   */
  HandleEditModalOpen(dbObj, changeType, initChanges = {}, action = null) {
    var activeOverrideObj = this.GetActiveOverrideObj(dbObj);
    var overrideObj = null;
    var onlyComment = false;
    if (activeOverrideObj != null) {
      overrideObj = activeOverrideObj;
      if (changeType === enumOverrideType.comment) onlyComment = true;
    }
    if (overrideObj == null) {
      const objType = dbObj.InferObjectType();
      overrideObj = this.CreateOverride(changeType, objType, dbObj.GetObjectRecordID(), "", []);
    }

    var displayObj = overrideObj.SmartGetFieldValue("displayObject");
    if (displayObj && !dbObj.fCloneBase) dbObj.fCloneBase = displayObj.Clone(true);

    // Now we allow opening even with conflicts to let user resolve
    //if (this.OverrideHasConflicts(overrideObj)) {
    //   this.handleConflict(overrideObj);
    // } else {
    this.handleRecord(overrideObj, dbObj, changeType, false, initChanges, false, action);
    // }
  }

  /**
   * @param {@param {import("../constants").TBoxActions | null} action } action
   * @returns
   */
  HandleEditMultiModalOpen(dbObjs, changeType, initChanges = {}, action = null) {
    // Create overrides for all selected positions
    const overrides = [];
    const dataObjects = [];

    for (const dbObj of dbObjs) {
      const activeOverrideObj = this.GetActiveOverrideObj(dbObj);
      let overrideObj = null;

      if (activeOverrideObj != null) {
        overrideObj = activeOverrideObj;
      } else {
        const objType = dbObj.InferObjectType();
        overrideObj = this.CreateOverride(changeType, objType, dbObj.GetObjectRecordID(), "", []);
      }

      const displayObj = overrideObj.SmartGetFieldValue("displayObject");
      if (displayObj && !dbObj.fCloneBase) {
        dbObj.fCloneBase = displayObj.Clone(true);
      }

      // Check for conflicts on any of the positions
      if (this.OverrideHasConflicts(overrideObj)) {
        // If any position has conflicts, handle it separately
        // TODO handle this from table view
        this.handleConflict(overrideObj);
        return;
      }

      overrides.push(overrideObj);
      dataObjects.push(dbObj);
    }

    // Call the multi-record handler if it exists, otherwise fall back to single edit for first position
    if (this.handleMultiRecord) {
      this.handleMultiRecord(overrides, dataObjects, changeType, initChanges, action);
    } else {
      // Fallback: edit first position only
      if (overrides.length > 0 && dataObjects.length > 0) {
        this.handleRecord(
          overrides[0],
          dataObjects[0],
          changeType,
          false,
          initChanges,
          false,
          action
        );
      }
    }
  }

  HandlePositionAssignments(dbObj) {
    const objType = dbObj.InferObjectType();
    if (objType !== enumObjectTypes.position) {
      console.warn("HandlePositionAssignments called on non-position object type");
      return;
    }

    var activeOverrideObj = this.GetActiveOverrideObj(dbObj);
    var overrideObj = null;
    if (activeOverrideObj != null) {
      overrideObj = activeOverrideObj;
    }
    if (overrideObj == null) {
      overrideObj = this.CreateOverride(
        enumOverrideType.change,
        enumObjectTypes.position,
        dbObj.GetObjectRecordID(),
        "",
        []
      );
    }

    var displayObj = overrideObj.SmartGetFieldValue("displayObject");
    if (displayObj && !dbObj.fCloneBase) dbObj.fCloneBase = displayObj.Clone(true);

    if (this.OverrideHasConflicts(overrideObj)) {
      this.handleConflict(overrideObj);
    } else {
      this.handleAssignments(overrideObj, dbObj);
    }
  }
}
